import { type FC, memo, useMemo, useState } from 'react';

import type { Props } from '@/components/select/PersonSelect/types';
import { MIN_CHARS_TO_SEARCH } from '@/constants/search';
import usePeople from '@/hooks/usePeople';
import type { PersonMinimal } from '@/types/person';
import { Avatar, Select } from '@/ui';
import type { OptionType } from '@/ui/components/Form/Select/types';
import formatPersonName from '@/utils/formatPersonName';

const formatSupportingInfo = (person: PersonMinimal): string | null => {
  const parts = [person.birthday && `DOB: ${person.birthday}`, person.jurisdiction_name].filter(
    Boolean
  );
  return parts.length > 0 ? parts.join(' | ') : null;
};

const toOption = (person: PersonMinimal): OptionType<string> => ({
  avatar: person.profile_image_url || null,
  label: formatPersonName({ person, withPronouns: false }),
  subLabel: formatSupportingInfo(person),
  value: person.id
});

// Search-only (no full roster to resolve an id against), so -- unlike
// JurisdictionSelect -- this holds the selected OptionType directly rather
// than an id. Pure/reusable: no membership or eligibility logic lives here.
const PersonSelect: FC<Props> = (props) => {
  const {
    ariaLabel,
    autoFocus,
    disabled,
    label = 'Member',
    placeholder = 'Search for a person...',
    showLabel
  } = props;
  const [search, setSearch] = useState('');
  const enabled = search.trim().length >= MIN_CHARS_TO_SEARCH;

  const { data, isFetching } = usePeople({
    enabled,
    params: { page: '1', search: search.trim() }
  });

  const searchOptions = useMemo(() => (data?.data?.results ?? []).map(toOption), [data]);

  // Narrow each branch to its own plain variable here (rather than inside the
  // useMemo below) so both can sit directly in a dependency array -- the
  // union can't be narrowed by property access alone once it's no longer
  // adjacent to the `multiple` check.
  const selectedOptionsProp = props.multiple ? props.selectedOptions : undefined;
  const selectedOptionProp = props.multiple ? undefined : props.selectedOption;

  // Keep the current selection visible even when it falls out of the latest
  // search results (e.g. right after picking it, before the query clears).
  const selected = useMemo<OptionType<string>[]>(
    () =>
      props.multiple
        ? (selectedOptionsProp ?? [])
        : [selectedOptionProp].filter((option): option is OptionType<string> => !!option),
    [props.multiple, selectedOptionsProp, selectedOptionProp]
  );

  const options = useMemo(() => {
    const byValue = new Map(searchOptions.map((option) => [option.value, option]));
    selected.forEach((option) => {
      if (!byValue.has(option.value)) byValue.set(option.value, option);
    });
    return [...byValue.values()];
  }, [searchOptions, selected]);

  const sharedProps = {
    ariaLabel: ariaLabel ?? label,
    autoFocus,
    disabled,
    label,
    missingOptionsText: !enabled
      ? `Type ${MIN_CHARS_TO_SEARCH}+ characters to search`
      : isFetching
        ? 'Searching...'
        : 'No matches',
    onSearch: setSearch,
    options,
    placeholder,
    showLabel
  };

  if (props.multiple) {
    return (
      <Select
        {...sharedProps}
        multiple
        selectedOptions={props.selectedOptions}
        onChange={props.onChange}
      />
    );
  }

  const leftSection = props.selectedOption ? (
    <Avatar
      src={
        typeof props.selectedOption.avatar === 'string' ? props.selectedOption.avatar : undefined
      }
      name={props.selectedOption.label}
      size="sm"
    />
  ) : undefined;

  return (
    <Select
      {...sharedProps}
      leftSection={leftSection}
      selectedOption={props.selectedOption}
      onChange={props.onChange}
    />
  );
};

export default memo(PersonSelect);
